import { useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "../Icon.jsx";

const STATUS_FILTERS = [
  { id: "ATTENTION", label: "Belum siap" },
  { id: "READY", label: "Siap direview" },
  { id: "PENDING", label: "Belum diperiksa" },
  { id: "CREATED", label: "DO sudah dibuat" },
];

const SOURCE_LABELS = {
  SALES_ORDER: "Pesanan Penjualan (SO)",
  ADVANCE_INVOICE: "Faktur Dimuka",
};

const STOCK_STATUS_LABELS = {
  BELUM_DICEK: "Belum dicek",
  BELUM_DIHITUNG: "Belum dihitung",
  AMAN: "Aman",
  KURANG: "Kurang",
  PERLU_MAPPING_UOM: "Perlu mapping UOM",
  PERLU_DATA_SKU: "Perlu data SKU",
  PERLU_CEK: "Perlu cek",
  TIDAK_ADA_STOK: "Tidak ada stok",
  UOM_BELUM_DIPETAKAN: "UOM belum dipetakan",
  SKU_LOGISTICS_BELUM_ADA: "Data SKU belum ada",
};

const READINESS_LABELS = {
  ATTENTION: "Belum siap",
  READY: "Siap direview",
  PENDING: "Belum diperiksa",
  CREATED: "DO sudah dibuat",
};

const GLOBAL_BLOCKER_CODES = new Set([
  "DO_DATE_REQUIRED",
  "DRIVER_ASSIGNMENT_REQUIRED",
  "VEHICLE_REQUIRED",
  "WAREHOUSE_MAPPING_REQUIRED",
]);

const numberFormatter = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 4 });

function formatQuantity(value) {
  if (value === null || value === undefined || value === "") return "—";
  return numberFormatter.format(Number(value) || 0);
}

function formatTimestamp(value) {
  if (!value) return "Belum pernah";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Belum pernah";
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function normalizeStatusClass(value, fallback = "unknown") {
  return String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
}

function getWriteRequestStatus(source) {
  return String(source?.writeCapability?.requestStatus || "").toUpperCase();
}

function getReadinessBucket(source, preparation) {
  const writeStatus = getWriteRequestStatus(source);
  if (
    ["CREATED", "DUPLICATE_LINKED"].includes(writeStatus) ||
    source?.readinessStatus === "ALREADY_CREATED" ||
    preparation?.doStatus === "DO_CREATED"
  ) {
    return "CREATED";
  }
  if (writeStatus) return "ATTENTION";
  if (
    source?.readinessStatus === "READY_TO_CREATE" ||
    source?.writeCapability?.itemEditorReady
  ) return "READY";
  if (source?.readinessStatus?.startsWith("BLOCKED_")) return "ATTENTION";
  return "PENDING";
}

function getShippingAddress(source, preparation) {
  const text = String(source?.shippingAddress || preparation?.shippingAddressText || "").trim();
  const area = String(preparation?.resolvedArea || "").trim();
  if (text && (!area || text.toLowerCase() !== area.toLowerCase())) return text;
  return area ? `Area: ${area}` : "Alamat kirim belum tersedia";
}

function buildWorkspaceSources(preparations, contract) {
  const prepById = new Map(preparations.map((item) => [String(item.id), item]));
  const prepByNumber = new Map(
    preparations.map((item) => [String(item.soNumber || ""), item]),
  );
  const includedPreparations = new Set();

  const fromContract = (contract?.sources || []).map((source) => {
    const preparation =
      (source.preparationId ? prepById.get(String(source.preparationId)) : null) ||
      prepByNumber.get(String(source.sourceNumber || "")) ||
      null;

    if (preparation) includedPreparations.add(String(preparation.id));

    return {
      key: source.key,
      source,
      preparation,
      sourceType: source.sourceType,
      sourceLabel:
        source.sourceLabel || SOURCE_LABELS[source.sourceType] || "Dokumen sumber",
      sourceNumber: source.sourceNumber,
      customerName: source.customerName,
      customerNumber: source.customerNumber,
      stopSequence: source.stopSequence,
      itemCount: source.items?.length || preparation?.itemCount || 0,
      readinessBucket: getReadinessBucket(source, preparation),
    };
  });

  const pendingPreparations = preparations
    .filter((preparation) => !includedPreparations.has(String(preparation.id)))
    .map((preparation) => ({
      key: `PENDING:${preparation.id}`,
      source: null,
      preparation,
      sourceType: "SALES_ORDER",
      sourceLabel: SOURCE_LABELS.SALES_ORDER,
      sourceNumber: preparation.soNumber,
      customerName: preparation.customerName,
      customerNumber: preparation.customerNumber,
      stopSequence: preparation.stopSequence,
      itemCount: preparation.itemCount || 0,
      readinessBucket: getReadinessBucket(null, preparation),
    }));

  return [...fromContract, ...pendingPreparations].sort(
    (left, right) =>
      (Number(left.stopSequence) || 999999) -
        (Number(right.stopSequence) || 999999) ||
      String(left.sourceNumber || "").localeCompare(
        String(right.sourceNumber || ""),
        "id",
      ),
  );
}

function getGlobalBlockerCodes(contract) {
  const unfinished = (contract?.sources || []).filter(
    (source) => source.readinessStatus !== "ALREADY_CREATED",
  );

  if (!unfinished.length) return new Set();

  const commonCodes = new Set(
    (unfinished[0]?.blockers || []).map((blocker) => blocker.code),
  );

  unfinished.slice(1).forEach((source) => {
    const sourceCodes = new Set((source.blockers || []).map((blocker) => blocker.code));
    [...commonCodes].forEach((code) => {
      if (!sourceCodes.has(code)) commonCodes.delete(code);
    });
  });

  return new Set([...commonCodes].filter((code) => GLOBAL_BLOCKER_CODES.has(code)));
}

function getAttentionLabel(entry, globalBlockerCodes) {
  const writeStatus = getWriteRequestStatus(entry.source);
  if (["VALIDATING", "SENDING", "RECONCILING"].includes(writeStatus)) {
    return "Sedang diproses";
  }
  if (writeStatus === "RECONCILIATION_REQUIRED") return "Perlu pemeriksaan";
  if (writeStatus === "FAILED") return "Gagal diproses";

  if (entry.readinessBucket !== "ATTENTION") {
    return READINESS_LABELS[entry.readinessBucket] || "Belum diperiksa";
  }

  if (
    globalBlockerCodes.has("DRIVER_ASSIGNMENT_REQUIRED") ||
    globalBlockerCodes.has("VEHICLE_REQUIRED")
  ) {
    return "Menunggu data armada";
  }
  if (globalBlockerCodes.has("DO_DATE_REQUIRED")) return "Menunggu jadwal";
  if (globalBlockerCodes.has("WAREHOUSE_MAPPING_REQUIRED")) return "Menunggu gudang";

  const codes = new Set((entry.source?.blockers || []).map((blocker) => blocker.code));
  if (codes.has("STOCK_NOT_SAFE")) return "Stok tidak mencukupi";
  if (codes.has("SOURCE_CHANGED")) return "Dokumen berubah";
  if (codes.has("SHIPPING_ADDRESS_REQUIRED")) return "Alamat belum dipilih";
  if (codes.has("ITEMS_REQUIRED")) return "Detail belum tersedia";
  if ([...codes].some((code) => code.startsWith("ITEM_"))) return "Data item belum lengkap";
  return "Belum siap";
}

function getActionHint(entry, globalBlockerCodes) {
  const writeStatus = getWriteRequestStatus(entry.source);
  if (["VALIDATING", "SENDING", "RECONCILING"].includes(writeStatus)) {
    return "Proses sedang berjalan. Status akan diperbarui otomatis.";
  }
  if (writeStatus === "RECONCILIATION_REQUIRED") {
    return "Periksa hasil di Accurate. Jangan membuat ulang Delivery Order.";
  }
  if (writeStatus === "FAILED") {
    return "Hubungi administrator sebelum mencoba kembali.";
  }
  if (entry.readinessBucket === "READY") {
    return "Review item dan Qty DO sebelum melanjutkan.";
  }
  if (entry.readinessBucket === "PENDING") {
    return "Perbarui data untuk memeriksa kesiapan dokumen.";
  }
  if (entry.readinessBucket === "CREATED") return "";
  if (
    globalBlockerCodes.has("DRIVER_ASSIGNMENT_REQUIRED") ||
    globalBlockerCodes.has("VEHICLE_REQUIRED")
  ) {
    return "Lengkapi driver dan kendaraan pada Fleet Order untuk melanjutkan.";
  }
  if (globalBlockerCodes.has("WAREHOUSE_MAPPING_REQUIRED")) {
    return "Tentukan gudang Accurate pada panel kesiapan di atas.";
  }
  return "Selesaikan informasi yang masih kurang untuk melanjutkan.";
}

function getSourceActionLabel(entry) {
  const writeStatus = getWriteRequestStatus(entry.source);
  if (writeStatus === "RECONCILIATION_REQUIRED") return "Periksa hasil";
  if (["VALIDATING", "SENDING", "RECONCILING"].includes(writeStatus)) {
    return "Lihat proses";
  }
  if (writeStatus === "FAILED") return "Lihat hasil";
  return entry.readinessBucket === "READY"
    ? "Review item & Qty DO"
    : "Tinjau dokumen";
}

function ReadinessBadge({ bucket, label }) {
  return (
    <span className={`do-unified-status do-unified-status--${normalizeStatusClass(bucket)}`}>
      {label || READINESS_LABELS[bucket] || "Belum diperiksa"}
    </span>
  );
}

function StockBadge({ status }) {
  const normalized = String(status || "BELUM_DICEK").toUpperCase();
  return (
    <span className={`stock-readiness stock-readiness--${normalizeStatusClass(normalized)}`}>
      {STOCK_STATUS_LABELS[normalized] || status || "Belum dicek"}
    </span>
  );
}

function SourceItemsTable({
  entry,
  onItemWarehouseChange,
  onWarehouseEditingChange,
  stockSyncedAt,
  warehouseEditing = false,
  warehouseLocked = false,
  warehouses,
}) {
  const preparation = entry.preparation;
  const source = entry.source;
  const editable = Boolean(preparation && preparation.doStatus !== "DO_CREATED");
  const rows = preparation?.items?.length
    ? preparation.items.map((item) => ({
        key: item.id,
        itemName: item.itemName,
        itemNo: item.skuCode,
        quantity: item.quantity,
        unitName: item.unitName,
        warehouseId: item.warehouseId,
        warehouseName: item.warehouseName,
        required: item.requestedStockQtySmallUom,
        stock: item.stockQtySmallUom,
        shortage: item.shortageQty,
        smallestUnitName: item.smallestUnitName || item.unitName,
        stockStatus: item.stockStatus,
        warning: item.warningMessage,
        itemId: item.id,
      }))
    : (source?.items || []).map((item) => ({
        key: `${item.lineIndex}:${item.itemNo}`,
        itemName: item.itemName,
        itemNo: item.itemNo,
        quantity: item.quantity,
        unitName: item.itemUnitName,
        warehouseId: item.warehouseId,
        warehouseName: item.warehouseName,
        required: item.baseQuantityRequired,
        stock: item.baseStockAvailable,
        shortage: item.shortageBaseQuantity,
        smallestUnitName: item.smallestUnitName || item.itemUnitName,
        stockStatus: item.stockStatus,
        warning: item.stockWarning,
        itemId: null,
      }));

  const canEditWarehouse = editable && !warehouseLocked && rows.some((item) => item.itemId);

  return (
    <section className="do-unified-items" aria-label={`Item ${entry.sourceNumber}`}>
      <header>
        <div>
          <span>Detail item</span>
          <strong>{rows.length} item</strong>
        </div>
        <div className="do-unified-items__header-actions">
          <small>Stok diperbarui {formatTimestamp(stockSyncedAt)}</small>
          {canEditWarehouse && warehouseEditing && (
            <button
              className="secondary-button secondary-button--compact"
              onClick={() => onWarehouseEditingChange?.(false)}
              type="button"
            >
              Selesai mengatur
            </button>
          )}
        </div>
      </header>
      {warehouseEditing && (
        <div className="do-unified-items__edit-note" role="status">
          <Icon name="info" size={15} />
          <span>
            Pilih gudang pada item yang perlu diubah. Setiap pilihan disimpan
            otomatis dan stok langsung dihitung ulang.
          </span>
        </div>
      )}
      <div className="do-unified-items__wrap">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Pesanan</th>
              <th>Gudang</th>
              <th className="is-numeric" title="Jumlah pesanan setelah dikonversi ke satuan stok terkecil.">
                Kebutuhan
              </th>
              <th className="is-numeric">Stok tersedia</th>
              <th className="is-numeric">Kekurangan</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.key}>
                <td data-label="Item">
                  <strong>{item.itemName || item.itemNo || "—"}</strong>
                  <small>{item.itemNo || "Kode belum tersedia"}</small>
                </td>
                <td data-label="Pesanan">
                  {formatQuantity(item.quantity)} {item.unitName || ""}
                </td>
                <td data-label="Gudang">
                  {warehouseLocked ? (
                    <span className="do-unified-warehouse-waiting">
                      Menunggu gudang depot
                    </span>
                  ) : warehouseEditing && editable && item.itemId ? (
                    <select
                      aria-label={`Gudang ${item.itemName}`}
                      onChange={(event) =>
                        onItemWarehouseChange(item.itemId, event.target.value)
                      }
                      value={item.warehouseId || ""}
                    >
                      <option value="">Pilih gudang</option>
                      {warehouses
                        .filter((warehouse) => warehouse.active)
                        .map((warehouse) => (
                          <option
                            key={warehouse.warehouseId}
                            value={warehouse.warehouseId}
                          >
                            {warehouse.warehouseName}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <span>{item.warehouseName || "Belum ditentukan"}</span>
                  )}
                </td>
                <td className="is-numeric" data-label="Kebutuhan">
                  {formatQuantity(item.required)} {item.smallestUnitName || ""}
                </td>
                <td className="is-numeric" data-label="Stok tersedia">
                  {formatQuantity(item.stock)} {item.smallestUnitName || ""}
                </td>
                <td className="is-numeric" data-label="Kekurangan">
                  {formatQuantity(item.shortage)} {item.smallestUnitName || ""}
                </td>
                <td data-label="Status">
                  <StockBadge status={item.stockStatus} />
                  {item.warning && <small>{item.warning}</small>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="master-empty">
            Detail item belum tersedia. Perbarui data untuk melanjutkan.
          </div>
        )}
      </div>
    </section>
  );
}

function ProcessingHistory({ preparations }) {
  return (
    <section className="do-unified-history">
      <header>
        <div>
          <span>Riwayat pemrosesan</span>
          <strong>Delivery Order dalam Fleet Order</strong>
        </div>
        <small>{preparations.length} dokumen</small>
      </header>
      <div className="fleet-order-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Nomor dokumen</th>
              <th>Pelanggan</th>
              <th>Stop</th>
              <th>Status</th>
              <th>Nomor DO</th>
              <th>Terakhir diperbarui</th>
            </tr>
          </thead>
          <tbody>
            {preparations.map((preparation) => (
              <tr key={preparation.id}>
                <td><strong>{preparation.soNumber}</strong></td>
                <td>{preparation.customerName}</td>
                <td>{preparation.stopSequence || "—"}</td>
                <td>{preparation.doStatus === "DO_CREATED" ? "DO sudah dibuat" : "Belum dibuat"}</td>
                <td>{preparation.accurateDoNumber || "—"}</td>
                <td>{formatTimestamp(preparation.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function DoPreparationMasterDetail({
  busy,
  contract,
  onItemWarehouseChange,
  onSetWarehouseForAll,
  onReviewSource,
  onSelect,
  preparations,
  selectedPreparationId,
  stockSyncedAt,
  warehouses,
}) {
  const [mode, setMode] = useState("SOURCES");
  const [activeStatus, setActiveStatus] = useState("ATTENTION");
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [warehouseEditorOpen, setWarehouseEditorOpen] = useState(false);
  const [warehousePerItemEditing, setWarehousePerItemEditing] = useState(false);
  const [warehouseDraftId, setWarehouseDraftId] = useState("");
  const listRefs = useRef(new Map());

  const entries = useMemo(
    () => buildWorkspaceSources(preparations, contract),
    [contract, preparations],
  );

  const counts = useMemo(
    () =>
      entries.reduce(
        (result, entry) => ({
          ...result,
          [entry.readinessBucket]: result[entry.readinessBucket] + 1,
        }),
        { ATTENTION: 0, READY: 0, PENDING: 0, CREATED: 0 },
      ),
    [entries],
  );

  useEffect(() => {
    if (counts[activeStatus] > 0) return;
    const next = STATUS_FILTERS.find((filter) => counts[filter.id] > 0)?.id || "PENDING";
    setActiveStatus(next);
    setSelectedKey(null);
  }, [activeStatus, counts]);

  const filteredEntries = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return entries
      .filter((entry) => entry.readinessBucket === activeStatus)
      .filter((entry) => {
        if (!keyword) return true;
        return [entry.sourceNumber, entry.customerName, entry.sourceLabel]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword));
      });
  }, [activeStatus, entries, search]);

  const selected =
    filteredEntries.find((entry) => entry.key === selectedKey) ||
    filteredEntries[0] ||
    null;

  useEffect(() => {
    const nextPreparationId = selected?.preparation?.id || null;
    if (nextPreparationId !== selectedPreparationId) onSelect(nextPreparationId);
  }, [onSelect, selected, selectedPreparationId]);

  useEffect(() => {
    setWarehouseEditorOpen(false);
    setWarehousePerItemEditing(false);
    setWarehouseDraftId(
      String(
        selected?.preparation?.defaultWarehouseId ||
          selected?.preparation?.items?.[0]?.warehouseId ||
          "",
      ),
    );
  }, [selected?.key]);

  const globalBlockerCodes = useMemo(
    () => getGlobalBlockerCodes(contract),
    [contract],
  );
  const globalWarehouseBlocked = globalBlockerCodes.has("WAREHOUSE_MAPPING_REQUIRED");

  const selectedBlockers = useMemo(
    () =>
      (selected?.source?.blockers || []).filter(
        (blocker) =>
          !globalBlockerCodes.has(blocker.code) &&
          blocker.code !== "STOCK_NOT_SAFE",
      ),
    [globalBlockerCodes, selected],
  );
  const selectedStockNotices = useMemo(
    () =>
      (selected?.source?.blockers || []).filter(
        (blocker) => blocker.code === "STOCK_NOT_SAFE",
      ),
    [selected],
  );

  function changeStatus(status) {
    if (!counts[status]) return;
    setActiveStatus(status);
    setSearch("");
    setSelectedKey(null);
  }

  function selectEntry(entry) {
    setSelectedKey(entry.key);
    onSelect(entry.preparation?.id || null);
  }

  function handleListKeyDown(event) {
    if (
      !filteredEntries.length ||
      !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    const currentIndex = Math.max(
      0,
      filteredEntries.findIndex((entry) => entry.key === selected?.key),
    );
    let nextIndex = currentIndex;
    if (event.key === "ArrowDown") {
      nextIndex = Math.min(filteredEntries.length - 1, currentIndex + 1);
    }
    if (event.key === "ArrowUp") nextIndex = Math.max(0, currentIndex - 1);
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = filteredEntries.length - 1;
    const next = filteredEntries[nextIndex];
    selectEntry(next);
    window.requestAnimationFrame(() => listRefs.current.get(next.key)?.focus());
  }

  const selectedWarehouseRows = selected?.preparation?.items?.length
    ? selected.preparation.items
    : selected?.source?.items || [];
  const selectedWarehouseValues = [
    ...new Map(
      selectedWarehouseRows
        .filter((item) => item.warehouseId || item.warehouseName)
        .map((item) => [
          String(item.warehouseId || item.warehouseName),
          { id: item.warehouseId || null, name: item.warehouseName || "Belum ditentukan" },
        ]),
    ).values(),
  ];
  const selectedWarehouseName =
    selectedWarehouseValues.length === 1
      ? selectedWarehouseValues[0].name
      : selectedWarehouseValues.length > 1
        ? `${selectedWarehouseValues.length} gudang digunakan`
        : "Belum ditentukan";
  const selectedSingleWarehouseId =
    selectedWarehouseValues.length === 1
      ? String(selectedWarehouseValues[0].id || "")
      : "";
  const warehouseDraftDirty = Boolean(
    warehouseEditorOpen &&
      warehouseDraftId &&
      warehouseDraftId !== selectedSingleWarehouseId,
  );
  const warehouseInteractionBusy =
    busy === "warehouse" || warehousePerItemEditing || warehouseDraftDirty;

  return (
    <section className="do-unified-workspace" aria-label="Dokumen sumber Delivery Order">
      <header className="do-unified-workspace__header">
        <div>
          <span>Workspace Pembuatan DO</span>
          <h2>Dokumen sumber</h2>
          <p>Pilih dokumen, periksa kesiapan, lalu review Delivery Order.</p>
        </div>
        <div className="do-unified-workspace__mode" role="group" aria-label="Tampilan workspace">
          <button
            className={mode === "SOURCES" ? "is-active" : ""}
            onClick={() => setMode("SOURCES")}
            type="button"
          >
            Dokumen sumber <span>{entries.length}</span>
          </button>
          <button
            className={mode === "HISTORY" ? "is-active" : ""}
            onClick={() => setMode("HISTORY")}
            type="button"
          >
            Riwayat pemrosesan
          </button>
        </div>
      </header>

      {mode === "HISTORY" ? (
        <ProcessingHistory preparations={preparations} />
      ) : (
        <>
          <nav className="do-unified-workspace__filters" aria-label="Status kesiapan dokumen">
            {STATUS_FILTERS.map((filter) => (
              <button
                aria-pressed={activeStatus === filter.id}
                className={activeStatus === filter.id ? "is-active" : ""}
                disabled={!counts[filter.id]}
                key={filter.id}
                onClick={() => changeStatus(filter.id)}
                type="button"
              >
                {filter.label}<span>{counts[filter.id]}</span>
              </button>
            ))}
          </nav>

          <section className="do-unified-master-detail">
            <aside className="do-unified-master-detail__list">
              <label className="do-unified-search">
                <Icon name="search" size={16} />
                <input
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setSelectedKey(null);
                  }}
                  placeholder="Cari nomor dokumen atau pelanggan"
                  value={search}
                />
              </label>
              <div className="do-unified-list" onKeyDown={handleListKeyDown} role="listbox">
                {filteredEntries.map((entry) => (
                  <button
                    aria-selected={entry.key === selected?.key}
                    className={entry.key === selected?.key ? "is-active" : ""}
                    key={entry.key}
                    onClick={() => selectEntry(entry)}
                    ref={(node) => {
                      if (node) listRefs.current.set(entry.key, node);
                      else listRefs.current.delete(entry.key);
                    }}
                    role="option"
                    tabIndex={entry.key === selected?.key ? 0 : -1}
                    type="button"
                  >
                    <span className="do-unified-list__title">
                      <strong>{entry.sourceNumber}</strong>
                      <ReadinessBadge
                        bucket={entry.readinessBucket}
                        label={getAttentionLabel(entry, globalBlockerCodes)}
                      />
                    </span>
                    <span>{entry.customerName || "Pelanggan belum tersedia"}</span>
                    <small>
                      {entry.sourceLabel} · Stop {entry.stopSequence || "—"} · {entry.itemCount} item
                    </small>
                  </button>
                ))}
                {!filteredEntries.length && (
                  <div className="master-empty">Tidak ada dokumen pada status ini.</div>
                )}
              </div>
            </aside>

            {selected ? (
              <article className="do-unified-detail">
                <header className="do-unified-detail__header">
                  <div>
                    <span>{selected.sourceLabel}</span>
                    <h2>{selected.sourceNumber}</h2>
                    <p>{selected.customerName || "Pelanggan belum tersedia"}</p>
                  </div>
                  <ReadinessBadge
                    bucket={selected.readinessBucket}
                    label={getAttentionLabel(selected, globalBlockerCodes)}
                  />
                </header>

                {globalBlockerCodes.size > 0 && (
                  <div className="do-unified-global-lock" role="status">
                    <Icon name="info" size={16} />
                    <span>
                      Dokumen ini menunggu penyelesaian informasi Fleet Order pada panel kesiapan di atas.
                    </span>
                  </div>
                )}

                {selectedBlockers.length > 0 && (
                  <section className="do-unified-blockers" role="status">
                    <Icon name="warning" size={18} />
                    <div>
                      <strong>Yang perlu diselesaikan</strong>
                      <ul>
                        {selectedBlockers.map((blocker) => (
                          <li key={`${blocker.code}:${blocker.itemNo || ""}`}>
                            <b>{blocker.title}</b>
                            <span>{blocker.detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                )}

                {selectedStockNotices.length > 0 &&
                  selected.readinessBucket === "READY" && (
                    <section className="do-unified-stock-notice" role="status">
                      <Icon name="warning" size={18} />
                      <div>
                        <strong>
                          {selectedStockNotices.length} item perlu direview
                        </strong>
                        <span>
                          Stok tidak mencukupi untuk seluruh sisa SO. Item
                          dengan stok kosong tidak dimasukkan, sedangkan Qty
                          lainnya dapat diatur pada editor Delivery Order.
                        </span>
                      </div>
                    </section>
                  )}

                <dl className="do-unified-summary-list">
                  <div>
                    <dt>Pelanggan</dt>
                    <dd>{selected.customerName || "—"}</dd>
                    <small>{selected.customerNumber || "Nomor belum tersedia"}</small>
                  </div>
                  <div>
                    <dt>Alamat kirim</dt>
                    <dd>{getShippingAddress(selected.source, selected.preparation)}</dd>
                    <small>Stop {selected.stopSequence || "—"}</small>
                  </div>
                  <div>
                    <dt>Dokumen</dt>
                    <dd>{selected.sourceNumber}</dd>
                    <small>{selected.itemCount} item</small>
                  </div>
                </dl>

                {selected.readinessBucket === "CREATED" ? (
                  <div className="do-unified-completed">
                    <Icon name="check" size={20} />
                    <div>
                      <strong>Delivery Order sudah tersedia</strong>
                      <span>
                        {selected.source?.writeCapability?.accurateDoNumber ||
                          selected.source?.accurateDoNumber ||
                          selected.preparation?.accurateDoNumber ||
                          "Nomor DO belum tersedia"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                  <section className="do-unified-warehouse-card" aria-label="Pengaturan gudang Delivery Order">
                    <header>
                      <div>
                        <span>Gudang pengiriman</span>
                        <strong>{selectedWarehouseName}</strong>
                        <small>Gudang aktual yang digunakan oleh item pada dokumen ini.</small>
                      </div>
                      {selected.preparation &&
                        !globalWarehouseBlocked &&
                        !selectedSingleWarehouseId && (
                        <button
                          className="secondary-button secondary-button--compact"
                          onClick={() => {
                            setWarehouseEditorOpen((current) => !current);
                            setWarehousePerItemEditing(false);
                          }}
                          type="button"
                        >
                          {warehouseEditorOpen ? "Tutup pengaturan" : "Ubah gudang"}
                        </button>
                      )}
                    </header>

                    {warehouseEditorOpen && selected.preparation && !globalWarehouseBlocked && (
                      <div className="do-unified-warehouse-options">
                        <div className="do-unified-warehouse-option">
                          <div>
                            <strong>Satu gudang untuk semua item</strong>
                            <span>Gunakan pilihan ini bila seluruh item dikirim dari gudang yang sama.</span>
                          </div>
                          <div className="do-unified-warehouse-option__controls">
                            <select
                              aria-label="Gudang untuk seluruh item"
                              disabled={busy === "warehouse"}
                              onChange={(event) => setWarehouseDraftId(event.target.value)}
                              value={warehouseDraftId}
                            >
                              <option value="">Pilih gudang</option>
                              {warehouses
                                .filter((warehouse) => warehouse.active)
                                .map((warehouse) => (
                                  <option
                                    key={warehouse.warehouseId}
                                    value={warehouse.warehouseId}
                                  >
                                    {warehouse.warehouseName}
                                  </option>
                                ))}
                            </select>
                            <button
                              className="primary-button"
                              disabled={busy === "warehouse" || !warehouseDraftId}
                              onClick={async () => {
                                await onSetWarehouseForAll(
                                  selected.preparation.id,
                                  warehouseDraftId,
                                );
                                setWarehouseEditorOpen(false);
                              }}
                              type="button"
                            >
                              {busy === "warehouse" ? "Menyimpan…" : "Terapkan ke semua item"}
                            </button>
                          </div>
                        </div>

                        <div className="do-unified-warehouse-option do-unified-warehouse-option--alternate">
                          <div>
                            <strong>Gudang berbeda per item</strong>
                            <span>Gunakan hanya bila sebagian item benar-benar diambil dari gudang lain.</span>
                          </div>
                          <button
                            className="secondary-button"
                            disabled={busy === "warehouse"}
                            onClick={() => {
                              setWarehousePerItemEditing(true);
                              setWarehouseEditorOpen(false);
                            }}
                            type="button"
                          >
                            Atur per item
                          </button>
                        </div>
                      </div>
                    )}
                  </section>

                  <div className="do-unified-review-bar">
                    <div>
                      <strong>
                        {warehouseInteractionBusy
                          ? "Selesaikan pengaturan gudang"
                          : getActionHint(selected, globalBlockerCodes)}
                      </strong>
                      <span>
                        {warehouseInteractionBusy
                          ? "Terapkan perubahan atau selesaikan pengaturan per item sebelum review."
                          : "Review selalu menggunakan gudang dan stok terbaru yang terlihat pada tabel."}
                      </span>
                    </div>
                    {selected.source && (
                      <button
                        className={
                          selected.readinessBucket === "READY"
                            ? "primary-button"
                            : "secondary-button"
                        }
                        disabled={warehouseInteractionBusy}
                        onClick={() => onReviewSource(selected.source)}
                        type="button"
                      >
                        {getSourceActionLabel(selected)}
                      </button>
                    )}
                  </div>
                  </>
                )}

                <SourceItemsTable
                  entry={selected}
                  onItemWarehouseChange={onItemWarehouseChange}
                  onWarehouseEditingChange={setWarehousePerItemEditing}
                  stockSyncedAt={stockSyncedAt}
                  warehouseEditing={warehousePerItemEditing}
                  warehouseLocked={globalWarehouseBlocked}
                  warehouses={warehouses}
                />
              </article>
            ) : (
              <article className="do-unified-detail do-unified-detail--empty">
                <Icon name="document" size={28} />
                <strong>Tidak ada dokumen untuk ditampilkan.</strong>
                <span>Pilih status lain atau ubah kata pencarian.</span>
              </article>
            )}
          </section>
        </>
      )}
    </section>
  );
}
